(() => {
  const adapter = window.RivalThemeCards;
  if (!adapter) return;
  const template = () =>
    document.querySelector('template[data-rival-collection-workspace="1"]');

  // Page numbers, not in-memory cursors, are the collection navigation contract.
  const pageNumber = () => {
    const value = Number(new URL(location.href).searchParams.get("page") || 1);
    return Number.isSafeInteger(value) && value > 0 ? value : 1;
  };
  const canonical = document.querySelector('link[rel="canonical"]');
  const originalCanonical = canonical?.href;
  const nativeUrl = location.href;
  const originalTitle = document.title;
  const selectionKey = selection => selection
    ? selection.preciseVehicleId || selection.applicationId || selection.browseCollectionId || selection.token
    : null;
  let previousSelection;
  const syncMetadata = () => {
    if (!originalCanonical || !location.pathname.includes("/collections/")) return;
    const url = new URL(originalCanonical);
    const page = pageNumber();
    if (page > 1) url.searchParams.set("page", String(page));
    else url.searchParams.delete("page");
    canonical.href = url.href;
    const base = originalTitle.replace(/\s+[–—-]\s+Page\s+\d+(?=\s+[–—-]|$)/i, "");
    const suffix = base.lastIndexOf(" – ");
    document.title = page === 1 ? base : suffix < 0 ? `${base} – Page ${page}`
      : `${base.slice(0, suffix)} – Page ${page}${base.slice(suffix)}`;
  };
  const navigate = (url, replace = false) => {
    const nextState = { ...history.state };
    delete nextState.rivalPages;
    delete nextState.rivalToken;
    history[replace ? "replaceState" : "pushState"](nextState, "", url.pathname + url.search + url.hash);
    syncMetadata();
  };
  addEventListener("popstate", syncMetadata);

  const names = {
    vendors: "vf_brand",
    productTypes: "vf_type",
    availableOnly: "vf_available",
    minPrice: "vf_min",
    maxPrice: "vf_max",
  };
  // Shopify's vendor/type collections are dynamic, not saved collection IDs.
  // Their Liquid scope is fixed; clearing optional facets must never widen it.
  const fixedScope = () => {
    const data = template()?.dataset;
    return {
      ...(data?.scopeVendor ? { vendors: [data.scopeVendor] } : {}),
      ...(data?.scopeType ? { productTypes: [data.scopeType] } : {}),
    };
  };
  adapter.context = (root) =>
    root.dataset.context === "collection" && Object.keys(fixedScope()).length
      ? { pageContext: "general", collectionId: "", collectionHandle: "all" }
      : {};
  const read = () => {
    const p = new URL(location.href).searchParams;
    return {
      vendors: [
        ...new Set([
          ...p.getAll(names.vendors),
          ...p.getAll("filter.p.vendor"),
        ]),
      ],
      productTypes: [...new Set([
        ...p.getAll(names.productTypes),
        ...p.getAll("filter.p.product_type"),
      ])],
      // Availability and price facets were removed from the storefront.
      // Ignore historic filter URLs so hidden constraints cannot remain active.
      availableOnly: false,
      ...fixedScope(),
    };
  };
  const write = (filters, sort, replace = false) => {
    const url = new URL(location.href);
    Object.values(names).forEach((name) => url.searchParams.delete(name));
    [
      "filter.p.vendor",
      "filter.p.product_type",
      "filter.v.availability",
      "filter.v.price.gte",
      "filter.v.price.lte",
    ].forEach((name) => url.searchParams.delete(name));
    for (const [key, name] of Object.entries(names)) {
      if (key in fixedScope()) continue;
      const value = filters[key];
      if (Array.isArray(value))
        value.forEach((item) => url.searchParams.append(name, item));
      else if (value !== undefined && value !== false && value !== "")
        url.searchParams.set(name, value === true ? "1" : String(value));
    }
    url.searchParams.set("sort_by", sort);
    url.searchParams.delete("page");
    navigate(url, replace);
  };
  const allowedSorts = [
    "title-ascending",
    "title-descending",
  ];
  const currentSort = () => {
    const value = new URL(location.href).searchParams.get("sort_by");
    return allowedSorts.includes(value) ? value : "title-ascending";
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const labels = {
    brand: "Brand",
    type: "Product type",
    availability: "Availability",
    available: "Available to order",
    price: "Price",
    from: "From",
    to: "To",
    clear: "Clear filters",
    results: "matching products",
    previous: "Previous",
    next: "Next",
    page: "Page",
    nameAsc: "Name A–Z",
    nameDesc: "Name Z–A",
    result: "matching product",
    service:
      "The storefront filter service needs updating. Please retry shortly.",
    priceError: "The minimum price must not exceed the maximum price.",
    changed: "Product information changed. Please retry.",
    refreshing:
      "Price and availability filters will appear when catalogue data is ready.",
    market:
      "Price and availability filters are available for the UK GBP storefront.",
    missing:
      "The theme's vehicle-filter integration needs updating. You can retry or browse without vehicle filtering.",
    search: "Search",
    noOptions: "No matching options",
    refine: "Refine your results",
  };
  adapter.text = (key, fallback) => {
    const copy = template()?.content.querySelector("[data-rival-copy]");
    if (!copy) return fallback;
    try {
      return (
        JSON.parse(copy.textContent)[
          key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
        ] || fallback
      );
    } catch {
      return fallback;
    }
  };
  document.addEventListener("rival:selection-changed", (event) => {
    document.querySelectorAll("[data-rival-filter-drawer]").forEach((node) => {
      delete node.dataset.rivalFilterDrawer;
      delete node.dataset.rivalFilterFocus;
    });
    const selection = window.RivalStorefrontFitment?.storedSelection
      ? window.RivalStorefrontFitment.storedSelection()
      : event.detail;
    const url = new URL(location.href);
    // Automatic collection entry on a direct URL is not a shopper changing car.
    // Keep its page until the response tells us whether it is valid.
    const key = selectionKey(selection);
    if (key !== previousSelection && !selection?.collectionBrowsing) url.searchParams.delete("page");
    previousSelection = key;
    if (selection === null) Object.values(names).forEach((name) => url.searchParams.delete(name));
    navigate(url, true);
  });
  adapter.query = () => {
    const url = new URL(location.href);
    if (/^price-(ascending|descending)$/.test(url.searchParams.get("sort_by") || "")) {
      url.searchParams.set("sort_by", "title-ascending");
      url.searchParams.delete("page");
      navigate(url, true);
    }
    const t = template();
    const limit = Math.max(1, Math.min(100, Number(t?.dataset.pageSize) || 24));
    previousSelection = selectionKey(window.RivalStorefrontFitment?.storedSelection?.());
    // The service rejects offsets beyond its supported catalogue window.
    if ((pageNumber() - 1) * limit > 25000) {
      const url = new URL(location.href);
      url.searchParams.delete("page");
      navigate(url, true);
    }
    return {
      limit,
      after: "",
      offset: (pageNumber() - 1) * limit,
      country: t?.dataset.country || "",
      currency: t?.dataset.currency || "",
      categories: "live",
      filters: JSON.stringify(read()),
      sort: currentSort(),
    };
  };
  adapter.basePage = () => pageNumber();

  // The app calls offerBrowse on API/card failures. Restore the original native
  // grid immediately without clearing the customer's saved vehicle.
  const offerBrowse = adapter.offerBrowse;
  adapter.offerBrowse = (container, onBrowse) => {
    offerBrowse?.call(adapter, container, onBrowse);
    if (!template()) return;
    document.querySelectorAll('[data-rival-exact-hidden]').forEach(node => {
      node.hidden = false;
    });
    navigate(new URL(nativeUrl), true);
    document.title = originalTitle;
    if (canonical) canonical.href = originalCanonical;
    const notice = element("p", "", "Vehicle filtering is unavailable. The collection below is not filtered for your vehicle.");
    const first = element("a", "btn btn--secondary", "Browse collection from page 1");
    const url = new URL(nativeUrl);
    url.searchParams.delete("page");
    Object.values(names).forEach(name => url.searchParams.delete(name));
    first.href = url.pathname + url.search;
    container.append(notice, first);
  };
  adapter.renderResults = async ({
    root,
    container,
    data,
    selection,
    state,
    refresh,
    current,
  }) => {
    const t = template();
    if (!t) throw new Error(labels.missing);
    if (!data.page.facets)
      throw new Error(adapter.text("service", labels.service));
    const total = Number(data.page.total);
    if (!Number.isFinite(total) || total < 0) throw new Error(labels.changed);
    const requestedPage = pageNumber();
    const lastPage = Math.max(1, Math.ceil(total / adapter.query().limit));
    if (requestedPage > lastPage) {
      const url = new URL(location.href);
      url.searchParams.delete("page");
      navigate(url, true);
      await refresh([]);
      return;
    }
    // Invalid values such as page=0 or page=NaN must not survive in the URL.
    if (new URL(location.href).searchParams.has("page") &&
        new URL(location.href).searchParams.get("page") !== String(requestedPage)) {
      const url = new URL(location.href);
      if (requestedPage === 1) url.searchParams.delete("page");
      else url.searchParams.set("page", String(requestedPage));
      navigate(url, true);
    }
    if (total > 0 && !data.page.items.length) throw new Error(labels.changed);
    syncMetadata();
    const text = Object.fromEntries(
      Object.entries(labels).map(([key, fallback]) => [
        key,
        adapter.text(key, fallback),
      ]),
    );
    container.classList.add("rival-hyper-integrated");
    container.replaceChildren(t.content.cloneNode(true));
    const $ = (selector) => container.querySelector(selector);
    const filters = read(),
      page = data.page,
      facets = page.facets;
    const visibility =
      document.querySelector("[data-rival-collection-settings]")?.dataset || {};
    const visible = (key) =>
      data.filterSettings
        ? data.filterSettings[key === "showType" ? "showProductType" : key] !==
          false
        : visibility[key] !== "false";
    const sort = currentSort();
    const initialCountNodes = [
      ...document.querySelectorAll("[data-rival-results]"),
    ].map((node) => [node, node.hidden]);
    initialCountNodes.forEach(([node]) => {
      node.hidden = true;
    });
    const scope = container.closest(".collection");
    scope?.classList.add("rival-matching-active");
    let form, timer, headingCount;
    const dialog = $("dialog");
    const open = $(".rival-filter-open");
    const focusKey = root.dataset.rivalFilterFocus;
    const preserveDrawer = root.dataset.rivalFilterDrawer === "true";
    const setDrawer = (show) => {
      root.dataset.rivalFilterDrawer = String(show);
      open.setAttribute("aria-expanded", String(show));
      if (show) {
        $("[data-rival-mobile-filter-host]").append(form);
        if (!dialog.open) dialog.showModal();
      } else {
        if (dialog.open) dialog.close();
        $("[data-rival-filter-host]").append(form);
      }
    };
    const onResize = () => {
      if (matchMedia("(min-width: 1280px)").matches && dialog.open)
        setDrawer(false);
    };
    state.cleanup = () => {
      headingCount?.remove();
      container.classList.remove("rival-count-at-heading");
      clearTimeout(timer);
      removeEventListener("resize", onResize);
      if (dialog.open) dialog.close();
      scope?.classList.remove("rival-matching-active");
      initialCountNodes.forEach(([node, hidden]) => {
        if (node.isConnected) node.hidden = hidden;
      });
    };
    // The finder owns popstate refreshes; a second listener races its requests.
    addEventListener("resize", onResize);
    $("[data-rival-count]").textContent =
      data.mode && data.mode !== "EXACT"
        ? `${page.total} ${page.total === 1 ? "product" : "products"}`
        : `${page.total} ${page.total === 1 ? text.result : text.results}`;
    // RIVAL: show the live result count beneath the collection title on mobile.
    const heading = document.querySelector(".rival-collection-heading h1");
    if (heading) {
      headingCount = element("p", "rival-mobile-heading-count", $("[data-rival-count]").textContent);
      headingCount.setAttribute("role", "status");
      headingCount.setAttribute("aria-live", "polite");
      heading.insertAdjacentElement("afterend", headingCount);
      container.classList.add("rival-count-at-heading");
    }
    const select = $("[data-rival-sort]");
    const sorts = [
      ["title-ascending", text.nameAsc],
      ["title-descending", text.nameDesc],
    ];
    for (const [value, label] of sorts) {
      const option = element("option", "", label);
      option.value = value;
      select.append(option);
    }
    select.value = sort;
    select.closest("label").hidden = t.dataset.sorting === "false";
    const apply = (next, nextSort = select.value || sort) => {
      write(next, nextSort);
      refresh([]);
    };
    select.addEventListener("change", () => apply(read(), select.value));
    // Labels, membership and counts come from the live native filter service.
    // Liquid supplies Shopify's manual presentation order for this collection.
    const categoryNames = new Map((facets.categories || []).map(group => [group.value, group.label]));
    if (visible("showType") && !("productTypes" in fixedScope())) {
      const available = [...(facets.categories || [])];
      let order = [];
      try { order = JSON.parse(t.content.querySelector("[data-rival-category-order]")?.textContent || "[]"); } catch {}
      available.sort((a, b) => {
        const left = order.indexOf(a.value), right = order.indexOf(b.value);
        return (left < 0 ? 10000 : left) - (right < 0 ? 10000 : right) || a.label.localeCompare(b.label);
      });
      if (available.length) {
        const nav = element("div", "rival-quick-categories");
        nav.setAttribute("role", "group");
        nav.setAttribute("aria-label", facets.categoryLabel || "Shop by category");
        const list = element("div", "rival-quick-categories__list");
        const addButton = (label, count, value) => {
          const active = value ? filters.productTypes.includes(value) : filters.productTypes.length === 0;
          const button = element("button", "rival-quick-category");
          button.append(element("span", "rival-quick-category__label", label));
          button.setAttribute("aria-label", count === null ? label : `${label} (${count})`);
          button.type = "button";
          button.setAttribute("aria-pressed", String(active));
          if (count !== null) button.append(element("span", "rival-quick-category__count", String(count)));
          button.addEventListener("click", () => apply({...read(), productTypes: active || !value ? [] : [value]}));
          list.append(button);
        };
        addButton("All categories", null, null);
        available.forEach(group => addButton(group.label, group.count, group.value));
        nav.append(list);
        $("[data-rival-active-filters]").before(nav);
      } else if (facets.categorySyncError) {
        $("[data-rival-active-filters]").before(element("p", "text-subtext", "Categories are temporarily unavailable. Refresh to try again."));
      }
    }
    form = element("form", "face-form rival-facet-form");
    form.addEventListener("submit", (event) => event.preventDefault());
    const group = (title, key) => {
      const details = element(
        "details",
        "accordion-details facet-accordion-details",
      );
      details.open = root.dataset[`facetClosed${key}`] !== "true";
      details.addEventListener("toggle", () => {
        root.dataset[`facetClosed${key}`] = String(!details.open);
      });
      const summary = element(
        "summary",
        "accordion-details__summary flex items-center justify-between",
      );
      summary.append(
        element("span", "h6", title),
        element("span", "rival-facet-toggle", ""),
      );
      const content = element(
        "div",
        "accordion-details__content rival-facet-options",
      );
      details.append(summary, content);
      form.append(details);
      return content;
    };
    const checkbox = (parent, title, checked, count, key, onChange) => {
      const label = element("label", "rival-facet-option");
      const input = element("input");
      input.type = "checkbox";
      input.checked = checked;
      input.dataset.focusKey = key;
      input.disabled = count === 0 && !checked;
      input.addEventListener("change", () => {
        root.dataset.rivalFilterFocus = key;
        onChange(input.checked);
      });
      label.append(
        input,
        element("span", "rival-facet-label", title),
        element("span", "text-subtext rival-facet-count", String(count)),
      );
      parent.append(label);
    };
    for (const [key, title] of [["vendors", text.brand]]) {
      if (key in fixedScope()) continue;
      if (
        !visible(key === "vendors" ? "showBrand" : "showType") &&
        !filters[key].length
      )
        continue;
      const values = [...facets[key]];
      for (const selected of filters[key])
        if (!values.some((entry) => entry.value === selected))
          values.push({ value: selected, count: 0 });
      if (!values.length) continue;
      const content = group(title, key);
      values.forEach((entry) =>
        checkbox(
          content,
          entry.value,
          filters[key].includes(entry.value),
          entry.count,
          `${key}:${entry.value}`,
          (checked) => {
            const next = read();
            next[key] = checked
              ? [...new Set([...next[key], entry.value])]
              : next[key].filter((value) => value !== entry.value);
            apply(next);
          },
        ),
      );
    }
    const chips = $("[data-rival-active-filters]");
    for (const [key, label] of [
      ["availableOnly", text.available],
      ["minPrice", `${text.from}: ${filters.minPrice}`],
      ["maxPrice", `${text.to}: ${filters.maxPrice}`],
    ]) {
      if (filters[key] === undefined || filters[key] === false) continue;
      const chip = element(
        "button",
        "btn btn--secondary rival-filter-chip",
        `${label} ×`,
      );
      chip.type = "button";
      chip.addEventListener("click", () => {
        const next = read();
        delete next[key];
        apply(next);
      });
      chips.append(chip);
    }
    $("[data-rival-filter-host]").append(form);
    for (const [key, title] of [
      ["vendors", text.brand],
      ["productTypes", text.type],
    ]) {
      if (key in fixedScope()) continue;
      for (const value of filters[key]) {
        const chip = element(
          "button",
          "btn btn--secondary rival-filter-chip",
          `${key === "productTypes" ? "Category" : title}: ${key === "productTypes" ? (categoryNames.get(value) || (value.startsWith("gid://") ? "Unavailable category" : value)) : value} ×`,
        );
        chip.type = "button";
        chip.addEventListener("click", () => {
          const next = read();
          next[key] = next[key].filter((entry) => entry !== value);
          apply(next);
        });
        chips.append(chip);
      }
    }
    const hasFilters =
      (!fixedScope().vendors && filters.vendors.length) ||
      (!fixedScope().productTypes && filters.productTypes.length) ||
      filters.availableOnly ||
      filters.minPrice !== undefined ||
      filters.maxPrice !== undefined;
    if (hasFilters) {
      const clear = element("button", "btn btn--underline", text.clear);
      clear.type = "button";
      clear.addEventListener("click", () =>
        apply({ vendors: [], productTypes: [], availableOnly: false }),
      );
      chips.append(clear);
    }
    chips.hidden = !hasFilters;
    const filtering =
      (Boolean(data.filterSettings) || t.dataset.filtering !== "false") &&
      !!form.querySelector("details");
    $(".rival-matching-filters").hidden = !filtering;
    open.hidden = !filtering;
    $(".rival-matching-layout").classList.toggle(
      "rival-no-filters",
      !filtering,
    );
    open.addEventListener("click", () => setDrawer(true));
    $(".rival-filter-close").addEventListener("click", () => {
      setDrawer(false);
      open.focus();
    });
    $(".rival-filter-done").addEventListener("click", () => {
      setDrawer(false);
      open.focus();
    });
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      setDrawer(false);
      open.focus();
    });
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) setDrawer(false);
    });
    const nav = $("nav");
    const paging = (title, disabled, targetPage, rel) => {
      if (disabled) return;
      const link = element("a", "btn btn--secondary", title);
      const url = new URL(location.href);
      if (targetPage === 1) url.searchParams.delete("page");
      else url.searchParams.set("page", String(targetPage));
      link.href = url.pathname + url.search + url.hash;
      link.rel = rel;
      link.addEventListener("click", event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate(url);
        refresh([]);
      });
      nav.append(link);
    };
    paging(text.previous, requestedPage === 1, requestedPage - 1, "prev");
    nav.append(
      element(
        "span",
        "text-subtext",
        `${text.page} ${requestedPage}`,
      ),
    );
    paging(text.next, requestedPage >= lastPage, requestedPage + 1, "next");
    nav.hidden = lastPage === 1;
    $("[data-rival-empty]").hidden = page.total !== 0;
    const cards = await adapter.renderPage(page.items, state.controller.signal);
    if (!current()) return;
    if (cards.length !== page.items.length) throw new Error(text.changed);
    const grid = $("[data-rival-cards]");
    grid.replaceChildren(...cards);
    adapter.styleGrid(grid);
    container.setAttribute("aria-busy", "false");
    if (
      preserveDrawer &&
      filtering &&
      !matchMedia("(min-width: 1280px)").matches
    )
      setDrawer(true);
    if (focusKey)
      [...form.querySelectorAll("input")]
        .find((input) => input.dataset.focusKey === focusKey)
        ?.focus({ preventScroll: true });
    document.dispatchEvent(
      new CustomEvent("rival:results-rendered", {
        detail: {
          productIds: page.items.map((item) => item.productId),
          count: page.total,
        },
      }),
    );
  };
})();
